import React, { useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, useTheme, useMediaQuery,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Utils from "../util/Utils";

// ── Design tokens (match CompanyProfile) ───────────────────────────────────────
const C = {
  textPrimary:    "#0f1f35",
  textSecondary:  "#3d5166",
  textMuted:      "#7a92a8",
  bg:             "#ffffff",
  card:           "#ffffff",
  cardHeader:     "#f5f2ed",
  cardHeaderText: "#1a2744",
  border:         "#d0dcea",
  borderLight:    "#e8eef6",
  navyDark:       "#0f2d52",
  navy:           "#1a4075",
  navyMid:        "#2c5282",
  navyLight:      "#dce8f5",
  success:        "#0a8a7e",
};

const MONO = "'DM Mono', 'Fira Mono', 'Courier New', monospace";
const SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

const ACCENTS = ["#2c5282", "#4f46e5", "#0a8a7e", "#b86b00"];

// ── Shared page header (match profile page) ────────────────────────────────────
function PageHeader({ section, title, subtitle }) {
  return (
    <Box sx={{
      pb: 1.25, mb: 2,
      borderBottom: `1px solid ${C.borderLight}`,
    }}>
      <Box sx={{ borderLeft: `4px solid ${C.navy}`, pl: 1.5 }}>
        <Typography sx={{
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          fontWeight: 700,
          color: C.cardHeaderText, letterSpacing: "-0.01em",
          fontFamily: SANS, lineHeight: 1.2,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            mt: 0.35, fontSize: "0.9rem",
            color: C.textSecondary, fontFamily: SANS,
          }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Single projection panel ───────────────────────────────────────────────────
function ProjectionTable({ title, data, accent, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const rows    = Object.keys(data).map((k) => data[k]);
  const totalPV = rows.reduce((s, r) => s + (Number(r.discVal) || 0), 0);

  return (
    <Box sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "10px",
      overflow: "hidden",
      backgroundColor: C.bg,
      boxShadow: "0 1px 3px rgba(15,45,82,0.06)",
      transition: "box-shadow 0.15s",
      "&:hover": { boxShadow: "0 4px 12px rgba(15,45,82,0.1)" },
    }}>
      {/* Toggle header (match profile card header) */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between",
          px: 1.5, py: 1,
          cursor: "pointer", userSelect: "none",
          backgroundColor: C.cardHeader,
          borderBottom: `1px solid ${C.borderLight}`,
          transition: "background 0.12s",
          "&:hover": { backgroundColor: C.cardHeader, opacity: 0.95 },
        }}
      >
        {/* Left */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: "8px",
            backgroundColor: `${accent}18`,
            border: `1px solid ${C.borderLight}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 14 }}>
              {[6, 12, 8, 14].map((h, i) => (
                <Box key={i} sx={{
                  width: "3px", height: h, borderRadius: "2px",
                  backgroundColor: C.navy, opacity: 0.75,
                }} />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={{
              fontWeight: 700, fontSize: "1rem",
              color: C.cardHeaderText, fontFamily: SANS, lineHeight: 1.3,
            }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: C.textSecondary, fontFamily: SANS }}>
              {rows.length} year projection
            </Typography>
          </Box>
        </Box>

        {/* Right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{
              fontSize: "0.7rem", fontWeight: 700,
              letterSpacing: "0.04em",
              color: C.textSecondary, fontFamily: SANS,
            }}>
              Total PV
            </Typography>
            <Typography sx={{
              fontSize: "1rem", fontWeight: 700,
              color: C.cardHeaderText, fontFamily: MONO,
            }}>
              {Utils.format_currency_long(totalPV)}
            </Typography>
          </Box>
          <Box sx={{
            width: 28, height: 28, borderRadius: "6px",
            backgroundColor: C.bg,
            border: `1px solid ${C.borderLight}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}>
            <ExpandMoreIcon sx={{ fontSize: 18, color: C.navy }} />
          </Box>
        </Box>
      </Box>

      {/* Table */}
      {open && (
        <Box sx={{ backgroundColor: "#ffffff" }}>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: "transparent" }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: C.cardHeader }}>
                  {["Year", "Projected Amount", "Discount Factor", "Discounted Value"].map((h, i) => (
                    <TableCell key={h} align={i === 0 ? "center" : "right"} sx={{
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      color: C.cardHeaderText,
                      fontFamily: SANS,
                      py: 0.5,
                      px: i === 0 ? 1 : 1.25,
                      borderBottom: `1px solid ${C.borderLight}`,
                      width: i === 0 ? "12%" : "auto",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow
                    key={row.year}
                    sx={{
                      backgroundColor: i % 2 === 0 ? "#ffffff" : "#fafbfc",
                      transition: "background 0.1s",
                      "&:hover": { backgroundColor: C.cardHeader },
                      borderBottom: i < rows.length - 1 ? `1px solid ${C.borderLight}` : "none",
                    }}
                  >
                    <TableCell align="center" sx={{
                      fontWeight: 700, fontSize: "0.92rem",
                      color: accent, fontFamily: MONO,
                      py: "0.4rem", px: "0.75rem", border: "none",
                    }}>
                      {row.year}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: "0.9rem", fontWeight: 500,
                      color: C.textPrimary, fontFamily: MONO,
                      py: "0.4rem", px: "1rem", border: "none",
                    }}>
                      {Utils.format_currency_usd(row.projectedAmount)}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: "0.9rem",
                      color: C.textSecondary, fontFamily: MONO,
                      py: "0.4rem", px: "1rem", border: "none",
                    }}>
                      {row.discFact}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontSize: "0.92rem", fontWeight: 700,
                      color: C.success, fontFamily: MONO,
                      py: "0.4rem", px: "1rem", border: "none",
                    }}>
                      {Utils.format_currency_usd(row.discVal)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Total row */}
                <TableRow sx={{
                  backgroundColor: C.cardHeader,
                  borderTop: `1px solid ${C.borderLight}`,
                }}>
                  <TableCell colSpan={3} sx={{
                    fontWeight: 700, fontSize: "0.875rem",
                    color: C.cardHeaderText, fontFamily: SANS,
                    py: 0.5, px: 1, border: "none",
                  }}>
                    Total Present Value
                  </TableCell>
                  <TableCell align="right" sx={{
                    fontWeight: 700, fontSize: "1rem",
                    color: C.cardHeaderText, fontFamily: MONO,
                    py: 0.5, px: 1.25, border: "none",
                  }}>
                    {Utils.format_currency_long(totalPV)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IntrsicAccVal({ stockValues }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const projections = [
    { title: "10 Year Discounted Cashflow",  data: stockValues.tenDCFiv.projectedAmtList },
    { title: "20 Year Discounted Cashflow",  data: stockValues.twentyDCFiv.projectedAmtList },
    { title: "10 Year Discounted Earnings",  data: stockValues.tenDEiv.projectedAmtList },
    { title: "20 Year Discounted Earnings",  data: stockValues.twentyDEiv.projectedAmtList },
  ];

  return (
    <Box sx={{
      px: { xs: 1, sm: 1.5, md: 2 },
      py: 2,
      width: "100%",
      maxWidth: 1280,
      margin: "0 auto",
      boxSizing: "border-box",
      fontFamily: SANS,
      backgroundColor: C.bg,
    }}>
      <PageHeader
        title="Projected Amounts"
        subtitle="Year-by-year discounted cashflow & earnings projections"
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {projections.map((p, i) => (
          <ProjectionTable
            key={p.title}
            title={p.title}
            data={p.data}
            accent={ACCENTS[i]}
            defaultOpen={i === 0}
          />
        ))}
      </Box>
    </Box>
  );
}